import React, { useEffect, useRef, useState } from 'react';
import { FormControl } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import * as Constants from 'const';
import { updateSurface } from 'containers/Surfaces/actions';
import { getValidScreenName, isScreenNameInvalid } from 'utils/screenNameValidator';
import styles from '../../styles.module.scss';

type Props = {
  screenNames: Immutable.List<string>;
  screenId: string;
  toggleTooltip: (element?: React.RefObject<HTMLElement>, show?: boolean, text?: string) => void;
  initialScreenName: string;
  dataTestId: string;
  isReadOnly: boolean | undefined;
};

export const ScreenName: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const {
    screenNames,
    screenId,
    toggleTooltip,
    initialScreenName,
    dataTestId,
    isReadOnly,
  } = props;

  const [isScreenNameEditable, setIsScreenNameEditable] = useState(false);
  const [currentScreenName, setCurrentScreenName] = useState(initialScreenName);
  const inputElementRef = useRef<HTMLInputElement>(null);
  const screenTitleRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (isScreenNameEditable && inputElementRef.current) {
      inputElementRef.current.focus();
      inputElementRef.current.select();
      toggleTooltip(inputElementRef, true, initialScreenName);
    } else if (!isScreenNameEditable) {
      toggleTooltip();
    }
    // eslint-disable-next-line
    }, [isScreenNameEditable]);

  const enableEditMode = (): void => {
    setIsScreenNameEditable(true);
    setCurrentScreenName(initialScreenName);
  };

  const onChangeScreenName = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newScreenName = event.target.value;
    if (isScreenNameInvalid(newScreenName)) {
      return;
    }
    setCurrentScreenName(newScreenName);
    toggleTooltip(inputElementRef, true, newScreenName);
  };

  const saveScreenName = (): void => {
    const validScreenName = getValidScreenName(currentScreenName, screenNames, initialScreenName);
    if (validScreenName === initialScreenName) {
      setIsScreenNameEditable(false);

      return;
    }
    dispatch(updateSurface({ id: screenId, name: validScreenName }));
    setIsScreenNameEditable(false);
    setCurrentScreenName(validScreenName);
  };

  const onEnterPress = (event: React.KeyboardEvent): void => {
    if (event.key === Constants.KeyboardKey.ENTER) {
      saveScreenName();
    }
  };

  if (!isScreenNameEditable) {
    return (<span
      className={styles.screenDescription__title}
      onDoubleClick={enableEditMode}
      ref={screenTitleRef}
      onMouseEnter={(): void => toggleTooltip(screenTitleRef, true, initialScreenName)}
      onMouseLeave={(): void => toggleTooltip()}
      data-testid={`${dataTestId}-info-description-title-name`}
    >
      {initialScreenName}
    </span>);
  }

  return (
    <FormControl
      className={styles.input}
      maxLength={Constants.MAX_SCREEN_NAME_LENGTH}
      value={currentScreenName}
      onChange={onChangeScreenName}
      onBlur={saveScreenName}
      onKeyDown={onEnterPress}
      readOnly={isReadOnly}
      ref={inputElementRef}
      onMouseEnter={(): void => toggleTooltip(screenTitleRef, true, currentScreenName)}
      onMouseLeave={(): void => toggleTooltip()}
      data-testid={`${dataTestId}-info-description-title-input`}
    />
  );
};
